import { createStore } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import equalityFn from 'zustand/shallow';
import { IS_DEVELOPMENT } from '~/modules/config';
import { Routes } from '~/modules/routes';
import { getSearchResults } from '~/modules/services/searchService';
import { listingStore } from '~/modules/stores/listingStore';
import { routerStore, selectMatch } from '~/modules/stores/routerStore';
import { SearchParams } from '~/modules/stores/searchStore';
import { handleException } from '~/modules/utilities/error_utilities';
import { selectExtensionListing } from '~/modules/utilities/listing_utils';
import { SearchResult } from '~/schemas/search-schema-v1';

const CATEGORIES = [
    'Sales',
    'Finance',
    'Collaboration',
    'Analytics',
    'Marketing',
    'IT & Administration',
    'Enterprise Resource Planning',
    'Customer Service',
    'Human Resources',
];

const APP_TYPE = 'apps';

const WILCARD_KEYWORD = '*';

export enum OtherAppsLikeThisStatus {
    UNINITILIAZED = 'UNINITILIAZED',
    LOADED = 'LOADED',
    LOADING = 'LOADING',
    ERROR = 'ERROR',
}

export interface OtherAppsLikeThisState extends State {
    loadingStatus: OtherAppsLikeThisStatus;
    searchResults: SearchResult;
    searchParams: SearchParams;
    listingId: string;
}

const initialState: OtherAppsLikeThisState = {
    loadingStatus: OtherAppsLikeThisStatus.UNINITILIAZED,
    // @ts-expect-error * Automated Type Error Fix
    searchResults: null,
    // @ts-expect-error * Automated Type Error Fix
    searchParams: null,
    listingId: '',
};

export const otherAppsLikeThisStore = createStore(
    initialState,

    (set: SetState<OtherAppsLikeThisState>, _get: GetState<OtherAppsLikeThisState>) => ({
        async fetchSearchResults(listingId: string, searchParams: SearchParams, page = 0, pageSize = 4) {
            const { keyword, filterParams, type } = searchParams;

            try {
                set(function setLoading(draft) {
                    draft.loadingStatus = OtherAppsLikeThisStatus.LOADING;
                    draft.listingId = listingId;
                });

                // @ts-expect-error * Automated Type Error Fix
                const searchResults = await getSearchResults(keyword, type, page, pageSize, filterParams);

                set(function loadSearchResults(draft: OtherAppsLikeThisState) {
                    draft.searchResults = searchResults;
                    draft.loadingStatus = OtherAppsLikeThisStatus.LOADED;
                });
            } catch (error) {
                handleException(error, 'otherAppsLikeThisStore.fetchSearchResults', { searchParams });
                set(function failedLoadListings(draft: OtherAppsLikeThisState) {
                    draft.loadingStatus = OtherAppsLikeThisStatus.ERROR;
                });
            }
        },

        reset() {
            set(function resetStore() {
                return initialState;
            });
        },
    }),
    'otherAppsLikeThisStore',
    IS_DEVELOPMENT,
);

function checkShouldInitialize() {
    const { params, url } = selectMatch(routerStore.getState());

    if (Routes.LISTING !== url) {
        otherAppsLikeThisStore.reset();
        return;
    }

    // @ts-expect-error * Automated Type Error Fix
    const listingId = params.listingId;
    if (!listingId) {
        return;
    }

    const otherAppsLikeThisState = otherAppsLikeThisStore.getState();
    if (
        otherAppsLikeThisState.loadingStatus !== OtherAppsLikeThisStatus.UNINITILIAZED &&
        listingId === otherAppsLikeThisState.listingId
    ) {
        return;
    }

    const listingResponse = listingStore.getState().listings.get(listingId);

    // @ts-expect-error * Automated Type Error Fix
    const extensionListing = selectExtensionListing(listingResponse);

    const categories = extensionListing?.listingCategories;

    if (!categories) {
        return;
    }

    for (const category of categories) {
        if (CATEGORIES.includes(category)) {
            otherAppsLikeThisStore.fetchSearchResults(listingId, {
                keyword: WILCARD_KEYWORD,
                type: APP_TYPE,
                filterParams: { category: [category] },
            });
            break;
        }
    }
}

routerStore.subscribe(checkShouldInitialize, selectMatch, equalityFn);
listingStore.subscribe(checkShouldInitialize);
checkShouldInitialize();
